import React, { useState, useEffect } from 'react';
import { policiesApi } from '../services/api';
import { PolicyDto, CustomRuleDto } from '@shared/contracts';
import { Plus, Check, Shield } from 'lucide-react';

export const PolicyManagerPage: React.FC = () => {
  const [policies, setPolicies] = useState<PolicyDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Form state
  const [policyName, setPolicyName] = useState('');
  const [documentType, setDocumentType] = useState('all');
  const [selectedRules, setSelectedRules] = useState<string[]>([
    'hide_national_id',
    'hide_bank_account',
    'hide_phone',
    'hide_email',
  ]);
  const [customRuleName, setCustomRuleName] = useState('');
  const [customPattern, setCustomPattern] = useState('');
  const [customEntityType, setCustomEntityType] = useState('CUSTOM_ID');
  const [customRulesList, setCustomRulesList] = useState<CustomRuleDto[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const fetchPolicies = async () => {
    try {
      const data = await policiesApi.list();
      setPolicies(data || []);
    } catch (e) {
      console.error('Failed to fetch policies', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicies();
  }, []);

  const availableRules = [
    { id: 'hide_national_id', label: 'National ID / SSN / NRIC', desc: 'Canadian SIN, Singapore NRIC, Civil ID, Swedish OrgNr' },
    { id: 'hide_bank_account', label: 'Bank Account / IBAN', desc: 'ISO 13616 IBAN, Modulo 97 verification' },
    { id: 'hide_phone', label: 'Phone Numbers', desc: 'Mobile and landline numbers' },
    { id: 'hide_email', label: 'Email Addresses', desc: 'Work and personal email addresses' },
    { id: 'hide_passport', label: 'Passport & MRZ', desc: 'Passports, Visas, Residence Permits' },
    { id: 'hide_plate', label: 'Vehicle License Plates', desc: 'License plates & vehicle registration' },
    { id: 'hide_tax_tin', label: 'Tax Identification Numbers', desc: 'TFN, CUIT, Tax IDs' },
    { id: 'hide_civil_records', label: 'Civil Status Records', desc: 'Birth, Marriage, Death certificates' },
  ];

  const handleToggleRule = (ruleId: string) => {
    if (selectedRules.includes(ruleId)) {
      setSelectedRules(selectedRules.filter((r) => r !== ruleId));
    } else {
      setSelectedRules([...selectedRules, ruleId]);
    }
  };

  const handleAddCustomRule = () => {
    if (!customRuleName || !customPattern) return;
    setCustomRulesList([
      ...customRulesList,
      { name: customRuleName, pattern: customPattern, entityType: customEntityType },
    ]);
    setCustomRuleName('');
    setCustomPattern('');
  };

  const handleCreatePolicy = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      await policiesApi.create({
        name: policyName,
        documentType,
        rules: selectedRules,
        customRules: customRulesList,
        isDefault: true,
      });
      setMessage('Policy created and activated!');
      setShowCreateModal(false);
      setPolicyName('');
      fetchPolicies();
    } catch (err) {
      const error = err as Error;
      setMessage('Error creating policy: ' + (error.message || 'Creation failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-sky-600" />
            <span>Policy Engine</span>
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Configure privacy policies, obfuscation rules, and custom regex recognizers.
          </p>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 rounded-xl btn-primary text-xs font-semibold flex items-center gap-1.5 shadow-sm"
        >
          <Plus className="w-4 h-4" />
          <span>New Policy</span>
        </button>
      </div>

      {message && (
        <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium">
          {message}
        </div>
      )}

      {/* Policy List Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {policies.map((pol) => (
          <div key={pol.id} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-slate-900">{pol.name}</h3>
                  {pol.isDefault && (
                    <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Default Active
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  ID: {pol.id} | Target: {pol.documentType}
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase text-slate-400 font-mono">
                Active Rules ({pol.rules?.length || 0})
              </label>
              <div className="flex flex-wrap gap-1.5">
                {(pol.rules || []).map((r: string) => (
                  <span
                    key={r}
                    className="px-2.5 py-1 rounded-lg text-xs font-mono bg-slate-50 text-slate-700 border border-slate-200"
                  >
                    {r}
                  </span>
                ))}
              </div>
            </div>

            {pol.customRules && pol.customRules.length > 0 && (
              <div className="space-y-1.5 pt-2 border-t border-slate-100">
                <label className="text-[10px] font-semibold uppercase text-slate-400 font-mono">
                  Custom Recognizers ({pol.customRules.length})
                </label>
                <div className="space-y-1">
                  {pol.customRules.map((cr: CustomRuleDto, idx: number) => (
                    <div key={idx} className="p-2 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700 flex items-center justify-between">
                      <span className="font-medium">{cr.name} ({cr.entityType})</span>
                      <span className="text-slate-500 font-mono text-[10px]">{cr.pattern}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Modal for Policy Creation */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-xl bg-white p-6 rounded-2xl border border-slate-200 shadow-xl space-y-5 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-slate-900">Create New Policy</h2>

            <form onSubmit={handleCreatePolicy} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-700">Policy Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Standard Privacy Policy"
                  value={policyName}
                  onChange={(e) => setPolicyName(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-700">PII Recognizer Rules</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {availableRules.map((rule) => {
                    const isChecked = selectedRules.includes(rule.id);
                    return (
                      <div
                        key={rule.id}
                        onClick={() => handleToggleRule(rule.id)}
                        className={`p-3 rounded-xl border cursor-pointer transition-all ${
                          isChecked
                            ? 'bg-sky-50 border-sky-200 text-slate-900'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold">{rule.label}</span>
                          {isChecked && <Check className="w-4 h-4 text-sky-600" />}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">{rule.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Custom Rule Adder */}
              <div className="space-y-2 pt-2 border-t border-slate-100">
                <label className="text-xs font-semibold text-slate-700">Custom Regex Recognizer</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input
                    type="text"
                    placeholder="Rule Name"
                    value={customRuleName}
                    onChange={(e) => setCustomRuleName(e.target.value)}
                    className="bg-white border border-slate-300 rounded-xl px-3 py-1.5 text-xs text-slate-800"
                  />
                  <input
                    type="text"
                    placeholder="Regex Pattern"
                    value={customPattern}
                    onChange={(e) => setCustomPattern(e.target.value)}
                    className="bg-white border border-slate-300 rounded-xl px-3 py-1.5 text-xs text-slate-800 font-mono"
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomRule}
                    className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold border border-slate-200"
                  >
                    + Add Rule
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 rounded-xl text-slate-600 hover:text-slate-900 text-xs font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 rounded-xl btn-primary text-xs font-semibold shadow-sm"
                >
                  {saving ? 'Saving...' : 'Save & Activate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

